import { useStore } from '@tanstack/react-form';
import { cva, type VariantProps } from 'class-variance-authority';
import { Label } from '../../ui/label';
import { useFieldContext } from '../hooks/form';
import type { SetStateAction, Dispatch } from 'react';

const animatedPlaceholder = cva(
  'absolute left-3.5 -translate-y-1/2 px-1 font-medium transition-[opacity,top,font-size]',
  {
    variants: {
      variant: {
        selectInput: [
          'bg-white',
          'text-brand-medium-gray',
          'peer-aria-invalid:text-brand-error',
        ],
        textInput: [
          'not-aria-invalid:text-brand-medium-gray',
          'aria-invalid:text-brand-error',
          'text-base',
          'cursor-text',
          'bg-transparent',
          'peer-[&:not(:placeholder-shown)]:bg-white',
          'peer-[&:not(:placeholder-shown)]:text-xs/[1.5]',
          'peer-[&:not(:placeholder-shown)]:top-0',
        ],
        textArea: [
          'text-base',
          'text-brand-medium-gray',
          'group',
          'cursor-text',
          'bg-transparent',
          'peer-[&:not(:placeholder-shown)]:bg-white',
          'peer-[&:not(:placeholder-shown)]:text-xs/[1.5]',
          'peer-[&:not(:placeholder-shown)]:top-0',
        ],
      },
      openOrFilled: {
        true: 'top-0 text-xs/[1.5] z-10',
        false: null,
      },
    },
    compoundVariants: [
      {
        variant: 'textArea',
        openOrFilled: false,
        className: 'top-6',
      },
      {
        variant: 'textInput',
        openOrFilled: false,
        className: 'top-1/2',
      },
      {
        variant: 'selectInput',
        openOrFilled: false,
        className: 'top-1/2 text-base',
      },
    ],
    defaultVariants: {
      variant: 'textInput',
      openOrFilled: false,
    },
  },
);

export interface AnimatedPlaceholderProps
  extends VariantProps<typeof animatedPlaceholder> {
  placeholder: string;
  requiredField?: boolean;
  isOpen?: boolean;
  setIsOpen?: Dispatch<SetStateAction<boolean>>;
}

export const AnimatedPlaceholder = ({
  placeholder,
  requiredField = true,
  variant,
  isOpen = false,
  setIsOpen,
}: AnimatedPlaceholderProps) => {
  const { store, name, state } = useFieldContext<string>();
  const { errors, isDirty } = useStore(store, ({ meta }) => meta);

  return (
    <Label
      aria-invalid={isDirty && errors.length > 0}
      className={animatedPlaceholder({
        variant,
        openOrFilled: isOpen || !!state.value,
      })}
      htmlFor={name}
    >
      <span
        className={`${errors.length > 0 ? 'text-brand-error' : 'text-brand-medium-gray'}`}
        onClick={() => setIsOpen?.(!isOpen)}
      >
        {placeholder}
        {requiredField && <sup>*</sup>}
      </span>
    </Label>
  );
};
